import { useState, useMemo, useEffect } from 'react'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useOrderbookTracked } from '@/hooks/useAlerts'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { Loader2, Copy, ExternalLink, TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ExchangeBadge, ListingTagButton, IgnoreListButton } from '@/components/common'
import type { TrackedOrder } from '@/types'
import { FALLBACK_EXCHANGES } from '@/types'

type Exchange = string

interface ExchangeFilter {
  enabled: boolean
  futures: boolean
  spot: boolean
}

export default function OrderbookOverview() {
  const exchangeOptions = useExchangeOptions('orderbook')
  const [viewMode, setViewMode] = useState<'exchange' | 'ticker'>('exchange')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [filters, setFilters] = useState<Record<Exchange, ExchangeFilter>>(() => {
    const initial: Record<string, ExchangeFilter> = {}
    FALLBACK_EXCHANGES.forEach(ex => {
      initial[ex] = { enabled: true, futures: true, spot: true }
    })
    return initial as Record<Exchange, ExchangeFilter>
  })

  const { data, isLoading, error } = useOrderbookTracked()

  useEffect(() => {
    setFilters((prev) => {
      const next: Record<string, ExchangeFilter> = {}
      for (const exchange of exchangeOptions) {
        next[exchange] = prev[exchange] || { enabled: true, futures: true, spot: true }
      }
      return next
    })
  }, [exchangeOptions])

  // Normalize field names
  const orders = useMemo(() => {
    const rawOrders = (data?.orders || []) as TrackedOrder[]
    return rawOrders.map(order => ({
      ...order,
      size_usdt: order.usd_value ?? order.size_usdt ?? 0,
      last_seen: order.last_seen_ts ?? order.last_seen ?? 0,
      first_seen: order.first_seen_ts ?? order.first_seen ?? 0,
    }))
  }, [data])

  // Filter orders based on exchange/market toggles
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const exchange = (order.exchange || 'binance').toLowerCase() as Exchange
      const marketType = (order.market_type || 'futures').toLowerCase()
      const filter = filters[exchange]

      if (!filter?.enabled) return false
      if (marketType === 'futures' && !filter.futures) return false
      if (marketType === 'spot' && !filter.spot) return false

      return true
    })
  }, [orders, filters])

  // Group orders by exchange + market type
  const groupedByExchange = useMemo(() => {
    const grouped: Record<string, TrackedOrder[]> = {}

    for (const order of filteredOrders) {
      const exchange = (order.exchange || 'binance').toLowerCase()
      const marketType = (order.market_type || 'futures').toLowerCase()
      const key = `${exchange}_${marketType}`

      if (!grouped[key]) grouped[key] = []
      grouped[key].push(order)
    }

    // Sort all orders by price descending (highest at top) like a unified limit order view
    Object.values(grouped).forEach(list => {
      list.sort((a, b) => (b.price || 0) - (a.price || 0))
    })

    return grouped
  }, [filteredOrders])

  // Group orders by ticker (for ticker view)
  const groupedByTicker = useMemo(() => {
    const grouped: Record<string, TrackedOrder[]> = {}

    for (const order of filteredOrders) {
      const symbol = order.symbol || 'UNKNOWN'
      if (!grouped[symbol]) grouped[symbol] = []
      grouped[symbol].push(order)
    }

    // Sort tickers by total USD value
    const sorted = Object.entries(grouped)
      .map(([symbol, orders]) => ({
        symbol,
        orders,
        totalValue: orders.reduce((sum, o) => sum + (o.size_usdt || 0), 0)
      }))
      .sort((a, b) => b.totalValue - a.totalValue)

    return sorted
  }, [filteredOrders])

  // Calculate totals per exchange section
  const sectionTotals = useMemo(() => {
    const totals: Record<string, number> = {}
    for (const [key, orders] of Object.entries(groupedByExchange)) {
      totals[key] = orders.reduce((sum, o) => sum + (o.size_usdt || 0), 0)
    }
    return totals
  }, [groupedByExchange])

  const toggleExchange = (exchange: Exchange, enabled: boolean) => {
    setFilters(prev => ({
      ...prev,
      [exchange]: { ...(prev[exchange] || { enabled: true, futures: true, spot: true }), enabled }
    }))
  }

  const toggleMarketType = (exchange: Exchange, type: 'futures' | 'spot', enabled: boolean) => {
    setFilters(prev => ({
      ...prev,
      [exchange]: { ...(prev[exchange] || { enabled: true, futures: true, spot: true }), [type]: enabled }
    }))
  }

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text)
  }

  const formatValue = (value: number) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
    return value.toFixed(0)
  }

  const formatDuration = (firstSeenTs: number) => {
    const now = Date.now() / 1000
    const diff = Math.max(0, now - firstSeenTs)

    if (diff < 60) return `${Math.floor(diff)}s`
    if (diff < 3600) return `${Math.floor(diff / 60)}m`
    if (diff < 86400) {
      const hours = Math.floor(diff / 3600)
      const mins = Math.floor((diff % 3600) / 60)
      return `${hours}h ${mins}m`
    }
    const days = Math.floor(diff / 86400)
    const hours = Math.floor((diff % 86400) / 3600)
    return `${days}d ${hours}h`
  }

  const formatPrice = (price: number) => {
    if (price >= 1000) return `$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    if (price >= 1) return `$${price.toFixed(4)}`
    if (price >= 0.0001) return `$${price.toFixed(6)}`
    return `$${price.toFixed(8)}`
  }

  const getDeviationPct = (order: TrackedOrder) => {
    const limitPrice = order.price
    const currentPrice = (order as any).current_price
    if (!limitPrice || !currentPrice) return null
    return ((limitPrice - currentPrice) / currentPrice) * 100
  }

  // Render a single order card
  const OrderCard = ({ order }: { order: TrackedOrder }) => {
    const deviationPct = getDeviationPct(order)
    const isBid = order.side === 'bid'
    const firstSeen = order.first_seen || order.first_seen_ts || 0

    return (
      <div className={cn(
        "flex flex-col p-3 rounded-lg border bg-card min-w-[110px] max-w-[140px]",
        isBid ? "border-l-2 border-l-green-500" : "border-l-2 border-l-red-500"
      )}>
        {/* Ticker Name */}
        <div className="flex items-center gap-1 mb-2">
          <span
            className="font-semibold text-sm text-amber-500 cursor-pointer hover:text-purple-400 truncate"
            onClick={() => handleCopy(order.symbol.replace(/[-_]?USDT$/i, ''))}
            title="Click to copy"
          >
            {order.symbol.replace(/[-_]?USDT$/i, '')}
          </span>
          {isBid ? (
            <TrendingUp className="h-3 w-3 text-green-500 flex-shrink-0" />
          ) : (
            <TrendingDown className="h-3 w-3 text-red-500 flex-shrink-0" />
          )}
        </div>

        {/* Market Type Badge */}
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] px-1.5 py-0 mb-2 w-fit",
            order.market_type === 'spot'
              ? "text-cyan-400 border-cyan-500/50 bg-cyan-500/10"
              : "text-pink-400 border-pink-500/50 bg-pink-500/10"
          )}
        >
          {(order.market_type || 'FUTURES').toUpperCase()}
        </Badge>

        {/* Value */}
        <div className={cn(
          "text-lg font-bold",
          isBid ? "text-green-500" : "text-red-500"
        )}>
          {formatValue(order.size_usdt || 0)}
        </div>

        {/* Duration */}
        <div className="text-xs text-muted-foreground">
          {formatDuration(firstSeen)}
        </div>

        {/* Price + Deviation */}
        <div className="flex items-center gap-1 mt-2">
          <span className={cn(
            "w-2 h-2 rotate-45",
            isBid ? "bg-green-500" : "bg-red-500"
          )} />
          <span className="text-xs text-muted-foreground">
            {formatPrice(order.price)}
          </span>
        </div>

        {deviationPct !== null && (
          <div className={cn(
            "text-xs font-semibold",
            deviationPct >= 0 ? "text-green-500" : "text-red-500"
          )}>
            {deviationPct >= 0 ? '+' : ''}{deviationPct.toFixed(1)}%
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-1 mt-2 pt-2 border-t border-border/50">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => handleCopy(order.symbol.replace(/[-_]?USDT$/i, ''))}
          >
            <Copy className="h-3 w-3" />
          </Button>
          <a
            href={`https://www.coinglass.com/tv/${order.exchange.charAt(0).toUpperCase() + order.exchange.slice(1)}_${order.symbol}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost" size="icon" className="h-5 w-5">
              <ExternalLink className="h-3 w-3" />
            </Button>
          </a>
          <ListingTagButton
            symbol={order.symbol.replace(/[-_]?USDT$/i, '')}
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-xs"
            showLabel={false}
          />
          <IgnoreListButton
            symbol={order.symbol.replace(/[-_]?USDT$/i, '')}
            alertType="orderbook"
            variant="ghost"
            size="sm"
            className="h-5 px-1 text-xs"
            showLabel={false}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <Header title="Orderbook Overview" />

      <div className="flex-1 space-y-4 p-6">
        {/* Stats Row */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Tracked Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{filteredOrders.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unique Symbols</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{groupedByTicker.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Bids</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">
                {filteredOrders.filter(o => o.side === 'bid').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Asks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">
                {filteredOrders.filter(o => o.side === 'ask').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-xs text-muted-foreground uppercase tracking-wide">SHOW:</span>

              {exchangeOptions.map(exchange => (
                <div key={exchange} className="flex items-center gap-2 px-3 py-1.5 rounded bg-muted/50 border">
                  <Checkbox
                    id={`ex-${exchange}`}
                    checked={filters[exchange]?.enabled ?? true}
                    onCheckedChange={(checked: boolean) => toggleExchange(exchange, checked)}
                  />
                  <Label htmlFor={`ex-${exchange}`} className="text-sm font-medium capitalize cursor-pointer">
                    {exchange}
                  </Label>
                  <div className="flex gap-1 ml-1">
                    <button
                      onClick={() => toggleMarketType(exchange, 'futures', !filters[exchange].futures)}
                      className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded font-bold",
                        (filters[exchange]?.futures ?? true)
                          ? "bg-pink-500/20 text-pink-400 border border-pink-500/50"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      F
                    </button>
                    <button
                      onClick={() => toggleMarketType(exchange, 'spot', !filters[exchange].spot)}
                      className={cn(
                        "text-[10px] px-1.5 py-0.5 rounded font-bold",
                        (filters[exchange]?.spot ?? true)
                          ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      S
                    </button>
                  </div>
                </div>
              ))}

              <div className="ml-auto flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="auto-refresh"
                    checked={autoRefresh}
                    onCheckedChange={setAutoRefresh}
                  />
                  <Label htmlFor="auto-refresh" className="text-sm">Auto-Refresh</Label>
                </div>

                <div className="flex gap-1">
                  <Button
                    variant={viewMode === 'exchange' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('exchange')}
                  >
                    Exchange
                  </Button>
                  <Button
                    variant={viewMode === 'ticker' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('ticker')}
                  >
                    Ticker
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading/Error States */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="text-center text-destructive py-8">Failed to load orders</div>
        )}

        {/* Exchange View */}
        {!isLoading && !error && viewMode === 'exchange' && (
          <div className="grid gap-4 lg:grid-cols-2">
            {exchangeOptions.flatMap(exchange =>
              ['futures', 'spot'].map(marketType => {
                const key = `${exchange}_${marketType}`
                const orders = groupedByExchange[key] || []
                const total = sectionTotals[key] || 0
                const filter = filters[exchange] || { enabled: true, futures: true, spot: true }

                // Don't show section if filter is off or no orders
                if (!filter.enabled) return null
                if (marketType === 'futures' && !filter.futures) return null
                if (marketType === 'spot' && !filter.spot) return null
                if (orders.length === 0) return null

                return (
                  <Card key={key} className="overflow-hidden">
                    <CardHeader className="pb-3 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ExchangeBadge exchange={exchange} />
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] px-2 py-0.5",
                              marketType === 'spot'
                                ? "text-cyan-400 border-cyan-500/50 bg-cyan-500/10"
                                : "text-pink-400 border-pink-500/50 bg-pink-500/10"
                            )}
                          >
                            {marketType.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Total: </span>
                          <span className="font-bold text-primary">${formatValue(total)}</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex flex-wrap gap-3">
                        {orders.map((order, idx) => (
                          <OrderCard key={`${order.symbol}-${order.side}-${idx}`} order={order} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )
              })
            ).filter(Boolean)}
          </div>
        )}

        {/* Ticker View */}
        {!isLoading && !error && viewMode === 'ticker' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {groupedByTicker.slice(0, 50).map(({ symbol, orders, totalValue }) => {
              // Sort all orders by price descending (highest at top) like a unified limit order view
              const sortedOrders = [...orders].sort((a, b) => (b.price || 0) - (a.price || 0))

              return (
                <Card key={symbol}>
                  <CardHeader className="pb-2 border-b">
                    <div className="flex items-center justify-between">
                      <span
                        className="font-bold text-amber-500 cursor-pointer hover:text-purple-400"
                        onClick={() => handleCopy(symbol.replace(/[-_]?USDT$/i, ''))}
                      >
                        {symbol.replace(/[-_]?USDT$/i, '')}
                      </span>
                      <Badge variant="secondary" className="text-primary font-bold">
                        ${formatValue(totalValue)}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {[...new Set(orders.map(o => o.exchange))].map(ex => (
                        <ExchangeBadge key={ex} exchange={ex} size="sm" />
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-3 space-y-2">
                    {sortedOrders.map((order, idx) => {
                      const deviationPct = getDeviationPct(order)
                      const isBid = order.side === 'bid'

                      return (
                        <div
                          key={idx}
                          className={cn(
                            "flex items-center justify-between text-xs p-2 rounded",
                            isBid
                              ? "bg-gradient-to-r from-green-500/10 to-transparent border-l-2 border-l-green-500"
                              : "bg-gradient-to-r from-red-500/10 to-transparent border-l-2 border-l-red-500"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <ExchangeBadge exchange={order.exchange} size="sm" />
                            <span className={cn(
                              "font-bold",
                              isBid ? "text-green-500" : "text-red-500"
                            )}>
                              ${formatValue(order.size_usdt || 0)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-muted-foreground">{formatPrice(order.price)}</div>
                              {deviationPct !== null && (
                                <div className={cn(
                                  "font-semibold",
                                  deviationPct >= 0 ? "text-green-500" : "text-red-500"
                                )}>
                                  {deviationPct >= 0 ? '+' : ''}{deviationPct.toFixed(1)}%
                                </div>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                onClick={() => handleCopy(order.symbol.replace(/[-_]?USDT$/i, ''))}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                              <a
                                href={`https://www.coinglass.com/tv/${order.exchange.charAt(0).toUpperCase() + order.exchange.slice(1)}_${order.symbol}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button variant="ghost" size="icon" className="h-5 w-5">
                                  <ExternalLink className="h-3 w-3" />
                                </Button>
                              </a>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    {sortedOrders.length === 0 && (
                      <div className="text-xs text-muted-foreground text-center py-2">
                        No orders
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredOrders.length === 0 && (
          <div className="text-center text-muted-foreground py-12">
            No tracked orders found
          </div>
        )}
      </div>
    </div>
  )
}
